import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PanelTopOpen, X } from "lucide-react";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useAccessTier } from "@/hooks/use-access-tier";
import { useSettingsHub } from "./SettingsHubProvider";
import { useDialogKeyboard } from "@/lib/useDialogKeyboard";
import { SettingsStyleShell } from "./SettingsStyleShell";
import {
  SETTINGS_GROUPS,
  SETTINGS_SECTIONS,
  DEFAULT_SECTION_ID,
  findSection,
} from "./registry";

// The global Settings hub: one ContactCard-style modal (left nav rail + an
// autosaving right content pane) that hosts every settings surface. Opened from
// the sidebar "Settings" nav item (or a `?settings=<section>` deep link);
// replaces the retired per-page gear modals and the avatar dropdown.
//
// Chrome is sized in absolute px because the dashboard root font is 20px — rem
// text utilities would inflate here.
export function SettingsHub() {
  const {
    open,
    activeSection,
    activeTab,
    setActiveSection,
    setActiveTab,
    closeSettings,
    pendingGuardMessage,
    setPendingGuard,
  } = useSettingsHub();
  const { billingConfigured, authMe } = useAccessTier();
  const isPopout =
    typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("settingsPopout") === "true";
  // Authorized users (Task 1945): members working in an owner's workspace
  // never see the owner-only sections (Billing, Authorized Users).
  const actingForOwner = !!authMe?.actingFor;

  // (No "dashboard" stamp anymore — widget layout moved to the Dashboard's
  // inline Customize mode; there is no Dashboard section in the hub.)
  // (No "pipeline" stamp anymore — stage management moved inline onto the
  // Pipeline board; there is no Pipeline section in the hub.)
  // (No "tasks" stamp anymore — Task settings moved to the Tasks page's own
  // modal, which stamps the area itself; there is no Tasks section in the hub.)
  // (No "lead-capture" stamp anymore either — the section was removed July
  // 2026; the "Verify Content on: Contact" roadmap item ("websiteContact")
  // now stamps from the Marketing → Digital Receptionist Lead Capture tab.)

  // Sections filtered by runtime capability gates (Billing only shows when
  // Stripe is configured).
  const sections = useMemo(
    () =>
      SETTINGS_SECTIONS.filter((s) => {
        if (s.gate === "billing") return billingConfigured && !actingForOwner;
        if (s.gate === "owner") return !actingForOwner;
        return true;
      }),
    [billingConfigured, actingForOwner],
  );

  // If the active section is gated off, fall back to the default.
  const activeSafe = sections.some((s) => s.id === activeSection)
    ? activeSection
    : DEFAULT_SECTION_ID;
  const active = findSection(activeSafe);

  // Tabs for the active section, filtered by the same runtime gates as sections
  // (e.g. Billing only shows when Stripe is configured; owner-only tabs hidden
  // for authorized members working in an owner's workspace).
  const visibleTabs = useMemo(
    () =>
      (active?.tabs ?? []).filter((t) => {
        if (t.gate === "billing") return billingConfigured && !actingForOwner;
        if (t.gate === "owner") return !actingForOwner;
        return true;
      }),
    [active, billingConfigured, actingForOwner],
  );
  const isTabDisabled = useCallback(
    (t: { requiresSmsReady?: boolean }) => !!t.requiresSmsReady,
    [],
  );

  const activeTabSafe =
    visibleTabs.find((t) => t.id === activeTab && !isTabDisabled(t))?.id ??
    visibleTabs.find((t) => !isTabDisabled(t))?.id ??
    visibleTabs[0]?.id ??
    null;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const requestClose = useCallback(() => {
    if (isPopout) {
      window.close();
      return;
    }
    if (pendingGuardMessage) {
      pendingActionRef.current = () => closeSettings();
      setConfirmOpen(true);
      return;
    }
    closeSettings();
  }, [isPopout, pendingGuardMessage, closeSettings]);

  const requestSwitch = useCallback(
    (id: string) => {
      if (id === activeSafe) return;
      if (pendingGuardMessage) {
        pendingActionRef.current = () => {
          setPendingGuard(null);
          setActiveSection(id);
        };
        setConfirmOpen(true);
        return;
      }
      setActiveSection(id);
    },
    [activeSafe, pendingGuardMessage, setPendingGuard, setActiveSection],
  );

  const requestSwitchTab = useCallback(
    (id: string) => {
      if (id === activeTabSafe) return;
      if (pendingGuardMessage) {
        pendingActionRef.current = () => {
          setPendingGuard(null);
          setActiveTab(id);
        };
        setConfirmOpen(true);
        return;
      }
      setActiveTab(id);
    },
    [activeTabSafe, pendingGuardMessage, setPendingGuard, setActiveTab],
  );

  // Warn on a full page unload/refresh while a CSV is staged but not imported.
  useEffect(() => {
    if (!open || !pendingGuardMessage) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [open, pendingGuardMessage]);

  // Escape closes (via the same guard path). Kept as a window-level fallback
  // for when focus is outside the dialog; nested overlays that handle Escape
  // themselves (CustomDropdown, the stacked ConfirmDialog) stop propagation so
  // this never double-fires.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) requestClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, requestClose]);

  // Tab focus trap + initial focus + focus restore to the opener. Escape is
  // handled by the window listener above, but the hook's Escape path routes
  // through the same guarded close.
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const handleDialogKeyDown = useDialogKeyboard({
    open,
    onClose: requestClose,
    dialogRef,
  });

  if (!open || !active) return null;

  const activeTabDef = active.tabs?.find((t) => t.id === activeTabSafe);
  const popOutSettings = () => {
    const params = new URLSearchParams();
    params.set("settings", activeSafe);
    if (activeTabSafe) params.set("settingsTab", activeTabSafe);
    params.set("settingsPopout", "true");
    const popout = window.open(
      `${window.location.pathname}?${params.toString()}`,
      "incontrol-settings",
      "popup=yes,width=1180,height=860,resizable=yes,scrollbars=yes",
    );
    if (popout) {
      popout.focus();
      closeSettings();
    }
  };
  const BodyComponent = active.tabs ? activeTabDef?.Component : active.Component;
  // The registry remains canonical, while the shared shell receives the
  // runtime-filtered tab list for the active section (billing/owner gates).
  const shellSections = sections.map((section) =>
    section.id === activeSafe ? { ...section, tabs: visibleTabs } : section,
  );

  return createPortal(
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-0 ${isPopout ? "bg-background" : "bg-black/40 sm:p-4 sm:backdrop-blur-sm"}`}
      onClick={requestClose}
      onKeyDown={handleDialogKeyDown}
      data-testid="overlay-settings-hub"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        className={`flex h-full w-full flex-col overflow-hidden bg-card sm:flex-row ${isPopout ? "" : "shadow-2xl sm:h-[92vh] sm:max-w-6xl sm:rounded-xl sm:border sm:border-border"}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="modal-settings-hub"
      >
        <SettingsStyleShell
          groups={SETTINGS_GROUPS}
          sections={shellSections}
          activeSection={activeSafe}
          activeTab={activeTabSafe}
          onSectionChange={requestSwitch}
          onTabChange={requestSwitchTab}
          isTabDisabled={isTabDisabled}
          header={
            <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
              <div className="flex min-w-0 items-center gap-2">
                {active.icon && <active.icon size={18} className="shrink-0 text-foreground" />}
                <h2 className="truncate text-[18px] font-semibold text-foreground">
                  {active.label}
                </h2>
              </div>
              <div className="flex items-center gap-1">
              {!isPopout && (
                <button
                  type="button"
                  data-testid="button-pop-out-settings-hub"
                  aria-label="Pop Out Settings"
                  title="Pop Out Settings"
                  onClick={popOutSettings}
                  className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-semibold text-foreground/70 hover:bg-muted hover:text-foreground cursor-pointer transition-colors"
                >
                  <PanelTopOpen size={16} />
                  Pop Out
                </button>
              )}
              <button
                type="button"
                data-testid="button-close-settings-hub"
                aria-label="Close settings"
                onClick={requestClose}
                className="rounded-md p-1 text-foreground hover:bg-muted cursor-pointer transition-colors"
              >
                <X size={18} />
              </button>
              </div>
            </div>
          }
        >
          <Suspense fallback={<p role="status" className="ds-meta">Loading Settings…</p>}>
            {BodyComponent && <BodyComponent />}
          </Suspense>
        </SettingsStyleShell>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Discard staged import?"
        message={pendingGuardMessage ?? ""}
        variant="destructive"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        overlayClassName="z-[120]"
        onConfirm={() => {
          setConfirmOpen(false);
          const action = pendingActionRef.current;
          pendingActionRef.current = null;
          setPendingGuard(null);
          action?.();
        }}
        onCancel={() => {
          setConfirmOpen(false);
          pendingActionRef.current = null;
        }}
      />
    </div>,
    document.body,
  );
}
